import * as React from 'react';
import { OrthodonticReferralSendButton } from './OrthodonticReferralSendButton';
import { OrthodonticReferralSaveButton } from './OrthodonticReferralSaveButton';
import { OrthodonticReferralWithdrawButton } from './OrthodonticReferralWithdrawButton';
import { OrthodonticReferralTreatmentDecisionButtons } from './OrthodonticReferralTreatmentDecisionButtons';
import { OrthodonticReferralAnswerButton } from './OrthodonticReferralAnswerButton';
import { OrthodonticReferralSendTreatmentTwoButton } from './OrthodonticReferralSendTreatmentTwoButton';
import { OrthodonticReferralCopyButton } from './OrthodonticReferralCopyButton';
import { OrthodonticReferralMarkAsReadButton } from './OrthodonticReferralMarkAsReadButton';
import { OrthodonticReferralRevokeOrthodonticReferralTreatmentDecisionButton } from './OrthodonticReferralRevokeOrthodonticReferralTreatmentDecisionButton';
import { IOrthodonticReferral } from '../../../../models/IOrthodonticReferral';
import { IPatient } from '../../../../models/IPatient';

interface IOrthodonticReferralActionButtons {
    orthodonticReferral: IOrthodonticReferral;
    patient: IPatient
    changeSuccessMessage: (message: string) => void;
    changeErrorMessage: (message: string) => void;
    getOrthodonticReferral: (id: number) => void;
    onCopySuccess: (copiedData: IOrthodonticReferral, message: string) => void;
}

export const OrthodonticReferralActionButtons: React.FunctionComponent<IOrthodonticReferralActionButtons> = ({
    patient,
    orthodonticReferral,
    changeSuccessMessage,
    changeErrorMessage,
    getOrthodonticReferral,
    onCopySuccess }) => {

    const canEdit = orthodonticReferral.canEdit;
    const canClose = orthodonticReferral.canClose ?? false;
    const isReferralAnswerRequired = orthodonticReferral.isReferralAnswerRequired ?? true;
    const isICD10CodeAndTherapyRequired = orthodonticReferral.isICD10CodeAndTherapyRequired ?? false;
    const canWithdraw = orthodonticReferral.canWithdraw ?? false;
    const canMakeTreatmentDecision = orthodonticReferral.canMakeTreatmentDecision ?? false;
    const canSendOrthodonticReferralToConsultantDentalGroup = orthodonticReferral.canSendOrthodonticReferralToConsultantDentalGroup ?? false;
    const canRevokeOrthodonticReferralTreatmentDecision = orthodonticReferral.canRevokeOrthodonticReferralTreatmentDecision ?? false;
    const canSendOrthodonticReferralToJointConsultantGroup = orthodonticReferral.canSendOrthodonticReferralToJointConsultantGroup ?? false;
    const canSendTreatmentTwo = orthodonticReferral.canSendTreatmentTwo ?? false;
    const unreadAnswer = orthodonticReferral.unreadAnswer ?? false;
    const canCopy = orthodonticReferral.canCopy ?? false;
    const orthodonticReferralAdvancedConsultationNeedId = orthodonticReferral.orthodonticReferralAdvancedConsultationNeed?.orthodonticReferralAdvancedConsultationNeedId ?? 0;
    const isConsultantCareProvider = orthodonticReferral.isConsultantCareProvider ?? false;
    const awaitingDecisionMotivationText = orthodonticReferral.orthodonticReferralDecisionMotivation;
    const hasCompletedTreatmentCode = orthodonticReferral.hasCompletedTreatmentCode ?? true;
    const numberOfOrthodonticReferralTreatments = orthodonticReferral.treatments?.length ?? 0;
    const numberOfOrthodonticReferralICD10Codes = orthodonticReferral.orthodonticReferralICD10Codes?.length ?? 0;

    return (
        <>
            {
                canEdit || (canClose && isReferralAnswerRequired) || isICD10CodeAndTherapyRequired ?
                    <>
                        <OrthodonticReferralSaveButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId}
                            canClose={canClose} />
                    </>
                    : null
            }
            {
                canEdit ?
                    <>
                        <OrthodonticReferralSendButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId} />
                    </>
                    : null
            }
            {
                canWithdraw ?
                    <>
                        <OrthodonticReferralWithdrawButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId ?? 0} />
                    </>
                    : null
            }
            {
                canMakeTreatmentDecision || canSendOrthodonticReferralToConsultantDentalGroup || canSendOrthodonticReferralToJointConsultantGroup ?
                    <>
                        <OrthodonticReferralTreatmentDecisionButtons
                            orthodonticReferral={orthodonticReferral}
                            patient={patient}
                            changeSuccessMessage={changeSuccessMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            canMakeTreatmentDecision={canMakeTreatmentDecision}
                            canSendOrthodonticReferralToConsultantDentalGroup={canSendOrthodonticReferralToConsultantDentalGroup}
                            canSendOrthodonticReferralToJointConsultantGroup={canSendOrthodonticReferralToJointConsultantGroup}
                            orthodonticReferralAdvancedConsultationNeedId={orthodonticReferralAdvancedConsultationNeedId}
                            isConsultantCareProvider={isConsultantCareProvider}
                            awaitingDecisionMotivationText={awaitingDecisionMotivationText} />
                    </>
                    : null
            }
            {
                canSendTreatmentTwo ?
                    <>
                        <OrthodonticReferralSendTreatmentTwoButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId} />
                    </>
                    : null
            }
            {
                canClose || isICD10CodeAndTherapyRequired ?
                    <>
                        <OrthodonticReferralAnswerButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId ?? 0}
                            isReferralAnswerRequired={isReferralAnswerRequired}
                            hasCompletedTreatmentCode={hasCompletedTreatmentCode}
                            numberOfOrthodonticReferralICD10Codes={numberOfOrthodonticReferralICD10Codes}
                            numberOfOrthodonticReferralTreatments={numberOfOrthodonticReferralTreatments}
                            isICD10CodeAndTherapyRequired={isICD10CodeAndTherapyRequired} />
                    </>
                    : null
            }
            {
                unreadAnswer ?
                    <>
                        <OrthodonticReferralMarkAsReadButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId ?? 0}
                            getOrthodonticReferral={getOrthodonticReferral} />
                    </>
                    : null
            }
            {
                canCopy ?
                    <>
                        <OrthodonticReferralCopyButton
                            changeErrorMessage={changeErrorMessage}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId ?? 0}
                            patientGuid={patient.patientGuidId}
                            onSuccess={onCopySuccess} />
                    </>
                    : null
            }
            {
                canRevokeOrthodonticReferralTreatmentDecision ?
                    <>
                        <OrthodonticReferralRevokeOrthodonticReferralTreatmentDecisionButton
                            changeSuccessMessage={changeSuccessMessage}
                            changeErrorMessage={changeErrorMessage}
                            getOrthodonticReferral={getOrthodonticReferral}
                            orthodonticReferralId={orthodonticReferral.orthodonticReferralId ?? 0} />
                    </>
                    : null
            }
        </>
    )
}